/**
 * Decision Brief — Export Service
 *
 * Exports decided brief options to external systems via the existing
 * integrations engine, and into the priorities.ai Prioritization App
 * as items (the platform upsell path).
 *
 * The connector integrations (Notion, Jira) call the integrations-engine
 * REST API which is already wired for priority export. We adapt the Brief
 * decision output to the PriorityExportItem shape.
 */

import { INTEGRATIONS_ENGINE_URL } from '../../lib/engineUrls';
import { getSupabaseClient } from '../../lib/supabaseClient';
import type { BriefOptionWithProposal, BriefDecision, BriefCriteria } from '../../types/brief';
import { rankOptions } from './briefService';

// ── Shared export item type (matches integrations engine PriorityExportItem) ──

export interface BriefExportItem {
  id: string;
  label: string;
  description: string;
  rank: number;
  score?: number;
  category: string;
  toolName: string;
  sessionId: string;
}

/** Convert decided brief options into PriorityExportItem format */
export function toBriefExportItems(
  options: BriefOptionWithProposal[],
  criteria: BriefCriteria[],
  decision: BriefDecision,
  briefTitle: string
): BriefExportItem[] {
  const ranked = rankOptions(options, criteria);

  return ranked
    .filter((o) => decision.selected_option_ids.includes(o.id))
    .map((o, i) => ({
      id: o.id,
      label: o.name,
      description: [
        o.proposal?.rationale,
        o.proposal?.investment_summary ? `Investment: ${o.proposal.investment_summary}` : null,
        o.proposal?.expected_outcome ? `Outcome: ${o.proposal.expected_outcome}` : null,
        o.proposal?.top_risk ? `Risk: ${o.proposal.top_risk}` : null,
      ]
        .filter(Boolean)
        .join('\n\n'),
      rank: i + 1,
      score: o.weightedScore ?? undefined,
      category: 'Strategic Initiative',
      toolName: `Decision Brief: ${briefTitle}`,
      sessionId: `brief-${decision.brief_id}`,
    }));
}

// ── Notion export ─────────────────────────────────────────────────────────────

export interface NotionExportOptions {
  workspaceId: string;
  databaseId: string;
}

export async function exportToNotion(
  items: BriefExportItem[],
  opts: NotionExportOptions
): Promise<{ success: boolean; created: number; errors: string[] }> {
  const response = await fetch(`${INTEGRATIONS_ENGINE_URL}/notion/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: opts.workspaceId,
      databaseId: opts.databaseId,
      priorities: items,
      titleProperty: 'Name',
      descriptionProperty: 'Description',
      rankProperty: 'Rank',
      scoreProperty: 'Score',
      categoryProperty: 'Category',
    }),
  });

  if (!response.ok) {
    throw new Error(`Notion export failed: ${response.statusText}`);
  }

  const data = await response.json();
  return {
    success: data.success,
    created: data.created ?? 0,
    errors: data.errors ?? [],
  };
}

// ── Jira export ───────────────────────────────────────────────────────────────

export interface JiraExportOptions {
  workspaceId: string;
  projectKey: string;
  issueType?: string;
}

export async function exportToJira(
  items: BriefExportItem[],
  opts: JiraExportOptions
): Promise<{ success: boolean; created: number; errors: string[] }> {
  const response = await fetch(`${INTEGRATIONS_ENGINE_URL}/jira/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspaceId: opts.workspaceId,
      sessionId: items[0]?.sessionId,
      options: {
        projectKey: opts.projectKey,
        issueType: opts.issueType ?? 'Epic',
        generateEpics: true,
        addPriorityLabel: true,
        addRankComment: true,
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Jira export failed: ${response.statusText}`);
  }

  const data = await response.json();
  return {
    success: data.success,
    created: data.created ?? 0,
    errors: data.errors?.map((e: { message?: string }) => e.message ?? String(e)) ?? [],
  };
}

// ── Export to priorities.ai as items (platform upsell) ───────────────────────

export interface PlatformExportResult {
  itemsCreated: number;
  itemIds: string[];
}

/**
 * Creates prioritization items in the current workspace from decided brief options.
 * Uses the same items table that the Prioritization App reads.
 * The brief decision is recorded as the item's provenance source.
 */
export async function exportToPrioritizationPlatform(
  options: BriefOptionWithProposal[],
  decision: BriefDecision,
  briefTitle: string,
  workspaceId: string
): Promise<PlatformExportResult> {
  const client = getSupabaseClient();
  const selectedOptions = options.filter((o) =>
    decision.selected_option_ids.includes(o.id)
  );

  const rows = selectedOptions.map((o) => ({
    workspace_id: workspaceId,
    label: o.name,
    description: [
      o.proposal?.rationale,
      decision.selected_rationale
        ? `Decision rationale: ${decision.selected_rationale}`
        : null,
      o.proposal?.investment_summary
        ? `Investment: ${o.proposal.investment_summary}`
        : null,
      o.proposal?.expected_outcome ? `Expected outcome: ${o.proposal.expected_outcome}` : null,
    ]
      .filter(Boolean)
      .join('\n\n'),
    status: 'active',
    source: 'decision_brief',
    source_ref: decision.brief_id,
    attributes: {
      brief_title: briefTitle,
      brief_id: decision.brief_id,
      owner: o.owner_name,
      investment: o.proposal?.investment_summary,
      assumptions: o.proposal?.assumptions,
      top_risk: o.proposal?.top_risk,
    },
  }));

  const { data, error } = await client
    .from('items')
    .insert(rows)
    .select('id');

  if (error) throw new Error(`Failed to export to platform: ${error.message}`);

  return {
    itemsCreated: data?.length ?? 0,
    itemIds: data?.map((r: { id: string }) => r.id) ?? [],
  };
}
